
import { ErrorHandler, Inject, Injectable, InjectionToken, isDevMode } from '@angular/core';
import { ROLLBAR_CONFIG } from '@monsido/core/constants/rollbar-config.constant';
import { MonLogService } from '@monsido/services/mon-log-service/mon-log.service';
import Rollbar from 'rollbar';


export const RollbarService = new InjectionToken<Rollbar>('rollbar');

function isLocalDevMode (): boolean {
    return window.location.hostname === 'localhost' || isDevMode();
}

/** Items raised through this service come from our own code. */
function ownData (data?: Record<string, unknown>): Record<string, unknown> {
    return { ...data, inApp: true };
}

export function rollbarFactory (): Rollbar {
    return new Rollbar(ROLLBAR_CONFIG);
}

@Injectable()
export class RollbarErrorHandlerService implements ErrorHandler {
    constructor (@Inject(RollbarService) private rollbar: Rollbar, private monLogService: MonLogService) {
        this.rollbar.configure({
            enabled: !isLocalDevMode(),
        });
    }

    setConfigPayload (payload: Record<string, unknown>): void {
        const options = this.rollbar.options;
        options.payload = {
            ...options.payload,
            ...payload,
        };
        this.rollbar.configure(options);
    }

    public handleError (err: string | Record<string, string>): void {
        if (isLocalDevMode()) {
            // rethrow error on local dev environment so we can get the proper stack trace
            throw err;
            // this.monLogService.error(err as string | Error);
            // return;
        }

        this.rollbar.error((err as Record<string, string>)?.originalError || err);
    }

    public log (message: string, error?: Error, data?: Record<string, unknown>, callback?: () => void): void {
        if (isLocalDevMode()) {
            this.monLogService.log(message);
        } else {
            this.rollbar.log(message, error, ownData(data), callback);
        }
    }

    public info (message: string, error?: Error, data?: Record<string, unknown>, callback?: () => void): void {
        if (isLocalDevMode()) {
            this.monLogService.info(message);
        } else {
            this.rollbar.info(message, error, ownData(data), callback);
        }
    }

    public warn (message: string, error?: Error, data?: Record<string, unknown>, callback?: () => void): void {
        if (isLocalDevMode()) {
            this.monLogService.warn(message);
        } else {
            this.rollbar.warn(message, error, ownData(data), callback);
        }
    }

    public error (message: string, error?: Error, data?: Record<string, unknown>, callback?: () => void): void {
        if (isLocalDevMode()) {
            throw new Error(message);
        } else {
            this.rollbar.error(message, error, ownData(data), callback);
        }
    }

    public critical (message: string, error?: Error, data?: Record<string, unknown>, callback?: () => void): void {
        if (isLocalDevMode()) {
            throw new Error(message);
        } else {
            this.rollbar.critical(message, error, ownData(data), callback);
        }
    }
}
